import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Menu,
  notification,
  Progress,
  Row,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { PERFORMANCE_MASTER_METRIC, STATUS } from '../../helpers/constants';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from '../../helpers/endpoints';
import { reportsGenerate } from '../../store/requests/dashboard';

const { Text } = Typography;

const RegionalPerformanceCard = ({
  performanceData,
  metrics,
  selectedPeriodDate,
  selectedDirectorate,
  setReportTypeLoading,
  isRegionLevel,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [sortMetricData] = useState([
    PERFORMANCE_MASTER_METRIC.NON_RETURNED_AND_ON_TIME_COMPLETE_COLLECTION,
    PERFORMANCE_MASTER_METRIC.OTIF,
    PERFORMANCE_MASTER_METRIC.CANCELLATION_AND_RETURN,
    PERFORMANCE_MASTER_METRIC.COMPLAINT,
  ]);

  const handleClick = (key, title) => {
    history.push({
      pathname: `${PAGE_URLS.PERFORMANCE_INFO}/${title}`,
      state: {
        performanceCardKey: { key, title },
      },
    });
  };

  const sendDocumentToMail = async (reportType, title) => {
    const payload = {
      reportType,
      reportTimeFrame: selectedPeriodDate === 'daily' ? 'DAILY' : 'MONTHLY',
      ...(isRegionLevel
        ? { regionId: selectedDirectorate }
        : { directorateId: selectedDirectorate }),
    };

    try {
      setReportTypeLoading(true);
      await reportsGenerate(payload);
      notificationMessage(STATUS.SUCCESS, title);
    } catch (error) {
      setReportTypeLoading(false);
    } finally {
      setReportTypeLoading(false);
    }
  };

  const notificationMessage = (type, reportType) => {
    const reportTemplate = t(reportType);
    notification[type]({
      message: t('processCompleted'),
      description: t('reportsGenerateMessage', { reportTemplate }),
    });
  };

  const dataNotArriving = (data, metric) => {
    return !data?.performancePercentageDtos?.some(
      (d) =>
        d.metric === metric &&
        d.performancePercentage !== null &&
        d.performancePercentage !== undefined,
    );
  };

  const getCardContent = (
    title,
    value,
    color,
    targetValue,
    metric,
    reportType,
    notArriving,
    index,
    childData,
  ) => {
    const cardKey = `card-${metric}-${index}`;

    return (
      <Card
        className="fillCardVertically"
        title={
          <div style={{ textAlign: 'center' }}>
            <Text strong>{t(title || 'Unknown Metric')}</Text>
          </div>
        }
        bordered={false}
        actions={
          childData.length > 0
            ? [
                <div>
                  {childData
                    .filter((met) => met != null)
                    .map((met, idx) => (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                        key={idx}
                      >
                        <Text strong>
                          {t(met?.label || 'Unknown Child Metric')}
                        </Text>
                        <span
                          style={{
                            color: met?.color || '#000',
                            fontWeight: 'bold',
                            marginLeft: '10px',
                          }}
                        >
                          {`%${met?.performancePercentage ?? 'X'}`}
                        </span>
                        <div style={{ marginLeft: '10px' }}>
                          {menu(
                            met?.reportType,
                            met?.label || 'Unknown',
                            met?.metric,
                            idx,
                          )}
                        </div>
                      </div>
                    ))}
                </div>,
              ]
            : undefined
        }
      >
        <div style={{ display: 'grid', justifyContent: 'center' }}>
          <Progress
            type="circle"
            percent={notArriving ? 'X' : value}
            format={(percent) => <Text strong>{`%${percent}`}</Text>}
            strokeColor={notArriving ? undefined : color}
            strokeWidth={10}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <span style={{ color: color, fontWeight: 'bold' }}>
              {targetValue}
            </span>
            {menu(reportType, title || 'Unknown', metric, index)}
          </div>
        </div>
      </Card>
    );
  };

  const menu = (reportType, title, metric, index, size = 14) => {
    return (
      <Dropdown
        overlay={
          <Menu>
            <Menu.Item
              disabled={!reportType}
              key="1"
              onClick={(e) => {
                e.domEvent.stopPropagation();
                sendDocumentToMail(reportType, title);
              }}
            >
              <a>{t('sendEmail')}</a>
            </Menu.Item>
            <Menu.Item
              disabled={['RETURNED_ORDER', 'CANCELLED_ORDER'].includes(metric)}
              key="2"
              onClick={(e) => {
                e.domEvent.stopPropagation();
                handleClick(metric, title);
              }}
            >
              <a>{t('showDescription')}</a>
            </Menu.Item>
          </Menu>
        }
      >
        <Button
          type="text"
          icon={<InfoCircleOutlined style={{ fontSize: size }} />}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
    );
  };

  const findChildrenData = (data, children) => {
    return children.map((child) => {
      return data?.performancePercentageDtos?.find(
        (d) => d.metric === child.key,
      );
    });
  };

  const renderPerformanceCards = (data, metrics) => {
    return sortMetricData.map((metric, index) => {
      const item = data.performancePercentageDtos.find(
        (d) => d.metric === metric,
      );

      return (
        <Col md={24} lg={6} key={metric}>
          {getCardContent(
            item?.label || 'Data Not Found',
            item?.performancePercentage ?? 'X',
            item?.color ?? '#000000d9',
            `${t('target')} %${item?.targetPerformancePercentage ?? 'N/A'}`,
            metric,
            item?.reportType,
            dataNotArriving(data, metric),
            index,
            findChildrenData(
              data,
              metrics.find((m) => m.key === metric)?.children ?? [],
            ),
          )}
        </Col>
      );
    });
  };

  return (
    <Card className="performanceCard" bordered={false}>
      <Row gutter={[24, 24]}>
        {renderPerformanceCards(performanceData, metrics)}
      </Row>
    </Card>
  );
};

export default RegionalPerformanceCard;
